import { baseApi } from './api';
import type { ApiResponse } from '../types';

// Profile Types
export interface UserAvatar {
  url: string;
  s3Key: string;
  thumbnailUrl?: string;
  originalName: string;
  uploadedAt: string;
}

export interface UserPreferences {
  language: string;
  timezone: string;
  dateFormat: string;
  timeFormat: '12h' | '24h';
  theme: 'light' | 'dark' | 'system';
  notifications: {
    email: {
      projectUpdates: boolean;
      taskAssignments: boolean;
      meetingReminders: boolean;
      dailyDigest: boolean;
      weeklyReport: boolean;
    };
    push: {
      enabled: boolean;
      urgent: boolean;
      mentions: boolean;
    };
  };
}

export interface UserProfile {
  _id: string;
  name: string;
  firstName?: string;
  lastName?: string;
  email: string;
  mobileNumber?: string;
  countryCode?: string;
  companyName?: string;
  companyType?: string;
  role: 'user' | 'admin';
  isActive: boolean;
  isEmailVerified: boolean;
  avatar?: UserAvatar;
  pendingEmail?: string;
  preferences?: UserPreferences;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateProfileRequest {
  firstName?: string;
  lastName?: string;
  name?: string;
  mobileNumber?: string;
  countryCode?: string;
  companyName?: string;
  companyType?: string;
}

export interface RequestEmailChangeRequest {
  newEmail: string;
  password: string;
}

export interface VerifyEmailChangeRequest {
  otp: string;
}

export interface UserOrganization {
  id: string;
  name: string;
  role: string;
  joinedAt: string;
  projectCount: number;
}

export interface UserPermissions {
  globalRole: string;
  isAdmin: boolean;
  projectRoles: Array<{
    projectId: string;
    projectName: string;
    role: string;
    permissions: string[];
  }>;
}

export interface ActivityLogEntry {
  _id: string;
  userId: string;
  action: string;
  category: 'profile' | 'security' | 'authentication' | 'settings';
  description: string;
  metadata: {
    previousValue?: Record<string, unknown>;
    newValue?: Record<string, unknown>;
    changedFields?: string[];
    ipAddress?: string;
    userAgent?: string;
    device?: string;
    location?: {
      city?: string;
      country?: string;
    };
  };
  status: 'success' | 'failed' | 'pending';
  createdAt: string;
}

export interface GetActivityLogParams {
  category?: string;
  action?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  limit?: number;
}

export interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

// Profile API endpoints
export const profileApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    // Get user profile
    getProfile: builder.query<UserProfile, void>({
      query: () => '/auth/profile',
      providesTags: ['Profile'],
      transformResponse: (response: ApiResponse<{ user: UserProfile }>) => response.data.user,
    }),

    // Update user profile
    updateProfile: builder.mutation<UserProfile, UpdateProfileRequest>({
      query: data => ({
        url: '/auth/profile',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Profile'],
      transformResponse: (response: ApiResponse<{ user: UserProfile }>) => response.data.user,
    }),

    // Upload avatar
    uploadAvatar: builder.mutation<{ user: UserProfile; avatarUrl: string }, FormData>({
      query: formData => ({
        url: '/auth/profile/avatar',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['Profile'],
      transformResponse: (response: ApiResponse<{ user: UserProfile; avatarUrl: string }>) =>
        response.data,
    }),

    // Remove avatar
    removeAvatar: builder.mutation<UserProfile, void>({
      query: () => ({
        url: '/auth/profile/avatar',
        method: 'DELETE',
      }),
      invalidatesTags: ['Profile'],
      transformResponse: (response: ApiResponse<{ user: UserProfile }>) => response.data.user,
    }),

    // Request email change
    requestEmailChange: builder.mutation<{ maskedEmail: string }, RequestEmailChangeRequest>({
      query: data => ({
        url: '/auth/profile/email/change',
        method: 'POST',
        body: data,
      }),
      transformResponse: (response: ApiResponse<{ maskedEmail: string }>) => response.data,
    }),

    // Verify email change
    verifyEmailChange: builder.mutation<UserProfile, VerifyEmailChangeRequest>({
      query: data => ({
        url: '/auth/profile/email/verify',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Profile'],
      transformResponse: (response: ApiResponse<{ user: UserProfile }>) => response.data.user,
    }),

    // Cancel email change
    cancelEmailChange: builder.mutation<void, void>({
      query: () => ({
        url: '/auth/profile/email/change',
        method: 'DELETE',
      }),
      invalidatesTags: ['Profile'],
    }),

    // Get user organizations
    getOrganizations: builder.query<UserOrganization[], void>({
      query: () => '/auth/profile/organizations',
      providesTags: ['ProfileOrganizations'],
      transformResponse: (response: ApiResponse<{ organizations: UserOrganization[] }>) =>
        response.data.organizations,
    }),

    // Get user permissions
    getPermissions: builder.query<UserPermissions, void>({
      query: () => '/auth/profile/permissions',
      providesTags: ['ProfilePermissions'],
      transformResponse: (response: ApiResponse<{ permissions: UserPermissions }>) =>
        response.data.permissions,
    }),

    // Get activity log
    getActivityLog: builder.query<
      { activities: ActivityLogEntry[]; pagination: PaginationInfo },
      GetActivityLogParams
    >({
      query: params => ({
        url: '/auth/profile/activity',
        params,
      }),
      providesTags: ['ProfileActivity'],
      transformResponse: (
        response: ApiResponse<{ activities: ActivityLogEntry[]; pagination: PaginationInfo }>
      ) => response.data,
    }),

    // Get preferences
    getPreferences: builder.query<UserPreferences, void>({
      query: () => '/auth/profile/preferences',
      providesTags: ['ProfilePreferences'],
      transformResponse: (response: ApiResponse<{ preferences: UserPreferences }>) =>
        response.data.preferences,
    }),

    // Update preferences
    updatePreferences: builder.mutation<UserProfile, Partial<UserPreferences>>({
      query: data => ({
        url: '/auth/profile/preferences',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Profile', 'ProfilePreferences'],
      transformResponse: (response: ApiResponse<{ user: UserProfile }>) => response.data.user,
    }),
  }),
  overrideExisting: false,
});

// Export hooks
export const {
  useGetProfileQuery,
  useLazyGetProfileQuery,
  useUpdateProfileMutation,
  useUploadAvatarMutation,
  useRemoveAvatarMutation,
  useRequestEmailChangeMutation,
  useVerifyEmailChangeMutation,
  useCancelEmailChangeMutation,
  useGetOrganizationsQuery,
  useGetPermissionsQuery,
  useGetActivityLogQuery,
  useGetPreferencesQuery,
  useUpdatePreferencesMutation,
} = profileApi;

// Export endpoints for manual use
export const {
  getProfile,
  updateProfile,
  uploadAvatar,
  removeAvatar,
  requestEmailChange,
  verifyEmailChange,
  cancelEmailChange,
  getOrganizations,
  getPermissions,
  getActivityLog,
  getPreferences,
  updatePreferences,
} = profileApi.endpoints;

// Utility functions
export const profileApiUtils = {
  invalidateProfile: (dispatch: any) => {
    dispatch(
      profileApi.util.invalidateTags([
        'Profile',
        'ProfileOrganizations',
        'ProfilePermissions',
        'ProfileActivity',
        'ProfilePreferences',
      ])
    );
  },

  resetApiState: (dispatch: any) => {
    dispatch(profileApi.util.resetApiState());
  },
};
